import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { deserialize } from '../../../../utility/serialization'
import { displayWithCurrency } from '../../../../utility/currency'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import TwoButtonSwitcher from '../../../common/TwoButtonSwitcher'
import { COMMON_ERRORS } from '../../../../misc/Constants'
import Loader from '../../../common/loader/Loader'
import { requestCommunityFinancialPerformanceReport } from '../../../../actions/reportsActions'
import FiltersButtonDropdown, { DATE_RANGE_FUNCS, DEFAULT_DATE_OPTION } from './FiltersButtonDropdown'
import FinancialPerformanceStackedChart from './FinancialPerformanceStackedChart'
import Placeholder from './Placeholder'
import { DATETIME_FORMAT, getMomentInCommunityTimezone } from '../../../../utility/datesHelper'
import moment from 'moment'

import classes from './FinancialPerformance.module.scss'
import businessDashboardClasses from './BusinessDashboard.module.scss'
import { ReactComponent as InfoCircleIcon } from '../../../../assets/images/common/icons/info-circle-icon-dark.svg'

const SingleStatistics = ({ data, label, currency, communityHasEnrollments, noRevenueMessage }) => {
  const growthPercentage = data.prevYearValue ? ((data.value - data.prevYearValue) / data.prevYearValue) : null
  const growthPercentageString = Intl.NumberFormat('en-US', { style: 'percent', signDisplay: 'exceptZero' })
    .format(growthPercentage)
  const useDash = data.value === 0 && communityHasEnrollments

  return (
    <div className={classnames('d-flex flex-column text-center', classes.statistics)}>
      <span className={classes.value}>
        {useDash ? '-'
          : currency
            ? displayWithCurrency(data.value, currency, true)
            : data.value
        }
      </span>
      <span className={classes.label}>{label}</span>
      {growthPercentage !== null && !useDash && (
        <span className={classnames(classes.growthPercentage, { [classes.negative]: growthPercentage < 0 })}>
          {growthPercentageString} from previous year
        </span>
      )}
      {noRevenueMessage && (
        <span className={classnames(classes.dataWithoutRevenueMessage)}>
          {noRevenueMessage}
        </span>
      )}
    </div>
  )
}

SingleStatistics.propTypes = {
  data: PropTypes.shape({
    value: PropTypes.number,
    prevYearValue: PropTypes.number,
  }),
  label: PropTypes.string,
  currency: PropTypes.string,
  communityHasEnrollments: PropTypes.bool,
  noRevenueMessage: PropTypes.string,
}

const FinancialPerformance = ({ isLoadingEnrollmentsPresence, communityHasEnrollments }) => {
  const community = useSelector(state => state.user.current_user.communities[0])
  const { loadingCategories } = useSelector(state => state.dashboard)

  const [filters, setFilters] = useState({
    dateOption: DEFAULT_DATE_OPTION,
    categoryOption: null,
    withRevenue: false,
  })
  const [isLoadingChartData, setIsLoadingChartData] = useState(true)
  const [data, setData] = useState()
  const noDataAvailable = useMemo(() => data?.monthlyData?.every(month => month.value === 0), [data?.monthlyData])

  useEffect(() => {
    setIsLoadingChartData(true)

    const { startDate, endDate } = DATE_RANGE_FUNCS[filters.dateOption.value].valueFunc()

    requestCommunityFinancialPerformanceReport({
      communityId: community?.id,
      startDate: getMomentInCommunityTimezone(moment(startDate), community?.timezone, DATETIME_FORMAT).toDate(),
      endDate: getMomentInCommunityTimezone(moment(endDate), community?.timezone, DATETIME_FORMAT).toDate(),
      courseCategoryId: filters.categoryOption?.value,
      withRevenue: filters.withRevenue,
    })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoadingChartData(false)
      })
      .catch(() => toast.error(COMMON_ERRORS[500]))
  }, [community, filters.categoryOption, filters.dateOption, filters.withRevenue])

  const handleTwoButtonSwitcherClick = () => {
    setFilters(prevFilters => ({ ...prevFilters, withRevenue: !prevFilters.withRevenue }))
  }

  const handleSubmitFilters = (newFilters) => {
    setFilters(prevState => ({ ...prevState, ...newFilters }))
  }

  const hasDataWithoutRevenue = !data?.coursesWithPackages && data?.estTotalRevenue.value === 0 && data?.totalRegistrations.value !== 0
  const isLoading = isLoadingEnrollmentsPresence || isLoadingChartData || loadingCategories

  return (
    <section className={businessDashboardClasses.widget}>
      <div className='d-flex justify-content-between align-items-center'>
        <div className='mb-3 d-flex align-items-center'>
          <h2 className={classnames('mb-0 mr-3', businessDashboardClasses.title)}>Financial Performance</h2>
          <InfoCircleIcon role='button' />
        </div>
        <FiltersButtonDropdown
          dateOption={filters.dateOption}
          categoryOption={filters.categoryOption}
          onSubmitFiltersClick={handleSubmitFilters}
        />
      </div>
      <TwoButtonSwitcher
        isRightSelected={filters.withRevenue}
        setIsSelectedRight={handleTwoButtonSwitcherClick}
        buttonsText={['Registrations', 'Revenue']}
      />
      {isLoading ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>Loading data</Loader>
        </div>
      ) : (
        <>
          <div className={classnames('my-4 py-3 px-4 d-flex justify-content-between', classes.statisticsWrapper)}>
            <SingleStatistics
              communityHasEnrollments={communityHasEnrollments}
              data={data.totalCourses}
              label='Total Programs'
            />
            <SingleStatistics
              communityHasEnrollments={communityHasEnrollments}
              data={data.estTotalRevenue} label='Est. Total Revenue'
              currency={community?.currency}
              noRevenueMessage={hasDataWithoutRevenue ? 'To track revenue, programs must use payment packages.' : null}
            />
            <SingleStatistics
              communityHasEnrollments={communityHasEnrollments}
              data={data.totalRegistrations}
              label='Total Registrations'
            />
            <SingleStatistics
              communityHasEnrollments={communityHasEnrollments}
              data={{ value: data.totalRegistrations.value === 0 ? 0 : data.estTotalRevenue.value / data.totalRegistrations.value }}
              label='Avg Price/Registration'
              currency={community?.currency}
              noRevenueMessage={hasDataWithoutRevenue ? 'To calculate average price, programs must use payment packages.' : null}
            />
          </div>
          <div className={classes.chartWrapper}>
            {noDataAvailable ? (
              <Placeholder
                communityHasEnrollments={communityHasEnrollments}
                isRevenue={filters.withRevenue}
                hasDataWithoutRevenue={hasDataWithoutRevenue}
              />
            ) : (
              <FinancialPerformanceStackedChart monthlyData={data.monthlyData} isRevenue={filters.withRevenue} />
            )}
          </div>

        </>
      )}
    </section>
  )
}

FinancialPerformance.propTypes = {
  isLoadingEnrollmentsPresence: PropTypes.bool,
  communityHasEnrollments: PropTypes.bool,
}

export default FinancialPerformance
